import { Card, CardBody, Flex, Icon, IconButton, Stack, Text } from '@chakra-ui/react';
import { uniqBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { BsChevronBarLeft, BsChevronBarRight } from 'react-icons/bs';
import { RiBook2Line } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';

import { usePortfolios } from '@/cip/api/portfolio';
import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { type OptimizationMeta } from '../../../_openapi/portfolio-optimizer';
import { useRequiredParams } from '../../../utils/useRequiredParams';
import { PageSpinner } from '../../../widgets/PageSpinner';
import { SearchInput } from '../../../widgets/SearchInput';
import { AlgoDetailsView } from './AlgoDetailsView';

export const TradeDetails = ({
    optimizationMeta,
    isLoading,
}: {
    optimizationMeta?: OptimizationMeta;
    isLoading?: boolean;
}) => {
    const { optimizationId } = useRequiredParams(['optimizationId']);
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedPId = searchParams.get('pId') ?? undefined;

    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();

    const { data: portfolioPage, isPending: portfoliosPending } = usePortfolios({
        dataVariant: 'preTrade',
        columnFilters: [
            {
                id: 'isSuspended',
                value: false,
            },
        ],
    });
    const isPending = portfoliosPending || isLoading;

    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    const [search, setSearch] = useState('');
    const portfolios = useMemo(() => {
        const accountGuids = optimizationMeta?.optimizationRequest?.accountGuids;
        if (!portfolioPage || !accountGuids) {
            return [];
        }

        let unique = uniqBy(
            portfolioPage.portfolios
                .map((portfolio) => portfolio.base)
                .filter(({ portfolioGuid }) => portfolioGuid != null && accountGuids.includes(portfolioGuid)),
            'pId',
        );

        if (search) {
            unique = unique.filter((portfolio) => portfolio.portfolioGuid?.includes(search));
        }

        return unique;
    }, [optimizationMeta?.optimizationRequest, portfolioPage, search]);

    const setPortfolioId = useCallback(
        (pId: string) => {
            setSearchParams(
                (params) => {
                    params.set('pId', pId);
                    return params;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    return (
        <>
            <Stack>
                <Flex gap={2}>
                    <Card
                        size="sm"
                        w={isPanelCollapsed ? '44px' : '375px'}
                        flexShrink={0}
                        transition="width 0.2s linear"
                    >
                        <CardBody>
                            <Stack>
                                <Flex justify={isPanelCollapsed ? 'center' : 'flex-end'}>
                                    <IconButton
                                        variant="ghost"
                                        size="xs"
                                        aria-label={
                                            isPanelCollapsed ? 'expand portfolio list' : 'collapse portfolio list'
                                        }
                                        icon={<Icon as={isPanelCollapsed ? BsChevronBarRight : BsChevronBarLeft} />}
                                        onClick={() => setIsPanelCollapsed((collapsed) => !collapsed)}
                                    />
                                </Flex>
                                {!isPanelCollapsed && (
                                    <>
                                        <SearchInput
                                            initialValue={search}
                                            debounceCallback={setSearch}
                                            placeholder="GUID filter"
                                        />
                                        <Stack gap={0} maxHeight="700px" overflow="scroll">
                                            {isPending ? (
                                                <PageSpinner />
                                            ) : portfolios.length > 0 ? (
                                                portfolios.map((pc) => (
                                                    <Flex key={pc.pId} alignItems="center" gap={2}>
                                                        <IconButton
                                                            variant="ghost"
                                                            size="xs"
                                                            aria-label="portfolio-details"
                                                            icon={<Icon boxSize={4} as={RiBook2Line} />}
                                                            onClick={(ev) => {
                                                                ev.stopPropagation();
                                                                viewPortfolio(pc.pId);
                                                            }}
                                                        />
                                                        <Text
                                                            fontSize="sm"
                                                            borderRadius="md"
                                                            px={2}
                                                            py={1}
                                                            cursor="pointer"
                                                            background={selectedPId === pc.pId ? 'teal' : 'transparent'}
                                                            onClick={() => setPortfolioId(pc.pId)}
                                                            _hover={{
                                                                background:
                                                                    selectedPId === pc.pId ? 'teal' : 'gray.700',
                                                            }}
                                                        >
                                                            {pc.portfolioGuid}
                                                        </Text>
                                                    </Flex>
                                                ))
                                            ) : (
                                                <Text fontSize="sm" color="gray.400">
                                                    <i>No results</i>
                                                </Text>
                                            )}
                                        </Stack>
                                    </>
                                )}
                            </Stack>
                        </CardBody>
                    </Card>

                    <Stack flex={1} overflow="hidden">
                        {selectedPId ? (
                            <AlgoDetailsView
                                optimizationId={Number(optimizationId)}
                                pId={selectedPId}
                                optimizationMeta={optimizationMeta}
                            />
                        ) : (
                            <Card size="sm">
                                <CardBody>
                                    <Text>
                                        <i>Select a portfolio to view algo details</i>
                                    </Text>
                                </CardBody>
                            </Card>
                        )}
                    </Stack>
                </Flex>
            </Stack>
            <PortfolioDrawer {...portfolioDrawerProps} />
        </>
    );
};
